import React from 'react';
import { MessageSquare, Loader2, Clock } from 'lucide-react';

/**
 * Left-panel thread list for the CRM Analysis page.
 *
 * Props:
 *   threads        {Array}    – chat_thread rows
 *   selectedId     {number}   – currently active thread id
 *   onSelect       {function} – (thread) => void
 *   loading        {boolean}
 */
export default function ChatHistoryPanel({ threads, selectedId, onSelect, loading }) {
  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <aside
      className="flex flex-col h-full border-r"
      style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF', width: '280px', minWidth: '280px' }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3 border-b"
        style={{ borderColor: '#D7B472', backgroundColor: '#FDFCF9' }}
      >
        <MessageSquare className="w-4 h-4" style={{ color: '#D7B472' }} />
        <span className="font-semibold text-sm" style={{ color: '#0A0A10' }}>
          Past Queries
        </span>
        {threads.length > 0 && (
          <span
            className="ml-auto text-xs px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: 'rgba(215,180,114,0.2)', color: '#8B6914' }}
          >
            {threads.length}
          </span>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-1">
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#D7B472' }} />
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 gap-1 px-4 text-center">
            <MessageSquare className="w-5 h-5 opacity-30" style={{ color: '#D7B472' }} />
            <p className="text-xs" style={{ color: '#0A0A10', opacity: 0.45 }}>
              No past queries yet
            </p>
          </div>
        ) : (
          threads.map((thread) => (
            <button
              key={thread.id}
              onClick={() => onSelect(thread)}
              className="w-full text-left px-4 py-3 transition-all border-b group"
              style={{
                borderColor: 'rgba(215,180,114,0.2)',
                backgroundColor: selectedId === thread.id
                  ? 'rgba(215,180,114,0.12)'
                  : 'transparent',
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <p
                  className="text-xs font-medium line-clamp-2 leading-relaxed"
                  style={{ color: '#0A0A10' }}
                >
                  {thread.userQuery || 'Query'}
                </p>
                {selectedId === thread.id && (
                  <span
                    className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1"
                    style={{ backgroundColor: '#D7B472' }}
                  />
                )}
              </div>
              <div className="flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3 opacity-40" style={{ color: '#0A0A10' }} />
                <span className="text-xs" style={{ color: '#0A0A10', opacity: 0.45 }}>
                  {formatDate(thread.created_at)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </aside>
  );
}
